/**
 * SQUAD_PACK wizard — admin creates a Squad bundle by picking already-
 * ingested teams + players from the SYSTEM catalog. Backend copies each
 * pick into PackTeam + PackPlayer template rows; the existing
 * _clone_pack_contents path clones into the buyer's tenant on purchase.
 *
 * Composition flow (option C — pick team, prefill master_squad, edit):
 *   1. Fill in pack details on the sidebar.
 *   2. Add one or more teams from the SYSTEM catalog. Each pick auto-
 *      prefills its master_squad players into a per-team roster card
 *      below; the curator can add / remove players per team.
 *   3. Hit Create & publish.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=SQUAD_PACK.
 *
 * 2026-07-18a — CSV upload → catalog picker.
 * 2026-07-18b — full-screen layout via WizardShell (sidebar + main),
 *               each picked team rendered as a large standalone card.
 */
import { useEffect, useState } from "react";
import {
  CheckCircle2, Users, ChevronDown, ChevronRight,
  Trash2, RotateCcw, Save,
} from "lucide-react";
import {
  createPack,
  updatePack,
  listCatalogTeams,
  listCatalogPlayers,
  selectSquadFromCatalog,
} from "../../api/owner.api";
import CatalogPicker from "../../components/marketplace/CatalogPicker";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";

const ROLE_TONE = {
  BATTER:        "bg-blue-500/15 text-blue-300 border-blue-400/30",
  BOWLER:        "bg-button-primary/20 text-button-primary border-button-primary/40",
  ALL_ROUNDER:   "bg-amber-500/15 text-amber-300 border-amber-400/30",
  WICKET_KEEPER: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};

function playerDisplay(p) {
  const parts = [p.first_name, p.middle_name, p.last_name].filter(Boolean);
  return p.nick_name || parts.join(" ").trim() || "(unnamed)";
}
function roleBadge(role) {
  if (!role) return "";
  if (role === "ALL_ROUNDER") return "AR";
  if (role === "WICKET_KEEPER") return "WK";
  return role.slice(0, 3);
}

export default function SquadPackWizard({ onClose, onCreated }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 99,
  });
  const [teamIds, setTeamIds] = useState([]);
  const [teamById, setTeamById] = useState({});
  const [playerIdsByTeam, setPlayerIdsByTeam] = useState({});
  const [collapsed, setCollapsed] = useState({}); // teamId → bool
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // 2026-07-31 — Publish is now owned exclusively by TOURNAMENT_BUNDLE.
  // Squad packs are saved as drafts, then added to a bundle in the
  // Tournament bundles tab and published from there. testMode +
  // publishPack call were removed with that flow.
  const [packId, setPackId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Reconcile per-team roster map when a team is removed via the chip X.
  useEffect(() => {
    setPlayerIdsByTeam((prev) => {
      const next = {};
      for (const id of teamIds) next[id] = prev[id] || [];
      return next;
    });
    setCollapsed((prev) => {
      const next = {};
      for (const id of teamIds) next[id] = prev[id] || false;
      return next;
    });
  }, [teamIds]);

  function handleTeamToggle(row, nowOn) {
    const id = String(row.id);
    if (nowOn) {
      setTeamById((prev) => ({ ...prev, [id]: row }));
      setPlayerIdsByTeam((prev) =>
        prev[id] && prev[id].length
          ? prev
          : { ...prev, [id]: (row.master_squad_ids || []).map(String) },
      );
    }
  }
  function setPlayersForTeam(teamId, ids) {
    setPlayerIdsByTeam((prev) => ({ ...prev, [teamId]: ids }));
  }
  function removeTeam(teamId) {
    setTeamIds((prev) => prev.filter((x) => x !== teamId));
  }
  function resetToMasterSquad(teamId) {
    const t = teamById[teamId];
    if (!t) return;
    setPlayerIdsByTeam((prev) => ({
      ...prev,
      [teamId]: (t.master_squad_ids || []).map(String),
    }));
  }
  function toggleCollapse(teamId) {
    setCollapsed((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  }

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  const totalPlayers = teamIds.reduce(
    (n, id) => n + (playerIdsByTeam[id]?.length || 0), 0,
  );

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  // Persist the current composition. Creates the pack on first call, PATCHes
  // details on subsequent calls, then always re-runs select-squad (the
  // backend wipes + re-writes so the founder's latest picks win). Returns
  // { pack, summary }.
  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "SQUAD_PACK",
      });
      pack = data;
      setPackId(pack.id);
    }

    const payloadTeams = teamIds.map((id) => ({
      team_id: id,
      player_ids: playerIdsByTeam[id] || [],
    }));
    const { data: summary } = await selectSquadFromCatalog(pack.id, payloadTeams);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (teamIds.length === 0) {
      setError("Pick at least one team from the catalog.");
      return false;
    }
    return true;
  }

  async function handleSave() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sidebar = (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="IPL 2026 — Full squads"
        shortDescriptionPlaceholder="10 IPL franchises with full 2026 squads"
      />

      <div className="pt-3 border-t border-border-default">
        <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
          Composition
        </div>
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-text-primary font-medium flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Teams
          </span>
          <span className="font-mono">{teamIds.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-text-primary font-medium">Players (total)</span>
          <span className="font-mono">{totalPlayers}</span>
        </div>
        {teamIds.length > 0 && (
          <ul className="space-y-1 text-xs text-text-secondary">
            {teamIds.map((tid) => {
              const t = teamById[tid];
              const n = playerIdsByTeam[tid]?.length || 0;
              return (
                <li key={tid} className="flex items-center justify-between gap-2">
                  <span className="truncate">{t?.short_name || t?.name || "Team"}</span>
                  <span className="font-mono">{n}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  const isSaved = Boolean(packId);
  return (
    <WizardShell
      eyebrow="Marketplace · SQUAD_PACK"
      title="New squad pack"
      onClose={onClose}
      busy={busy}
      error={error}
      sidebar={sidebar}
      primaryLabel={isSaved ? "Update draft" : "Save draft"}
      primaryIcon={<Save className="w-4 h-4" />}
      primaryDisabled={!detailsValid() || teamIds.length === 0}
      primaryTone="amber"
      onPrimary={handleSave}
    >
      <>
          {/* 1) Team picker */}
          <section className="space-y-3">
            <div>
              <h3 className="text-base font-semibold">Add teams from the catalog</h3>
              <p className="text-sm text-text-secondary mt-0.5">
                Only SYSTEM-owned teams the platform has already ingested are
                shown. Each pick prefills its master squad into an editable
                roster card below.
              </p>
            </div>
            <CatalogPicker
              fetchFn={(params) => listCatalogTeams(params)}
              selectedIds={teamIds}
              onChange={setTeamIds}
              onToggle={handleTeamToggle}
              placeholder="Search teams by name or short name…"
              emptyHint="No teams match. Try a different search."
              renderRow={(t) => (
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-medium">{t.name}</span>
                    {t.short_name && (
                      <span className="ml-2 text-xs text-text-secondary font-mono">
                        {t.short_name}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary shrink-0 flex items-center gap-2">
                    {t.team_type && <span>{t.team_type}</span>}
                    {t.nationality_name && <span>· {t.nationality_name}</span>}
                    <span className="inline-flex items-center gap-0.5">
                      <Users className="w-3 h-3" /> {(t.master_squad_ids || []).length}
                    </span>
                  </div>
                </div>
              )}
              renderChip={(t) => <span>{t.short_name || t.name}</span>}
            />
          </section>

          {/* 2) Per-team roster cards */}
          {teamIds.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-baseline justify-between">
                <h3 className="text-base font-semibold">
                  Team rosters
                  <span className="ml-2 text-sm font-normal text-text-secondary">
                    {totalPlayers} player{totalPlayers === 1 ? "" : "s"} across {teamIds.length} team{teamIds.length === 1 ? "" : "s"}
                  </span>
                </h3>
              </div>
              <div className="space-y-5">
                {teamIds.map((tid) => {
                  const t = teamById[tid];
                  const roster = playerIdsByTeam[tid] || [];
                  const isCollapsed = !!collapsed[tid];
                  return (
                    <div
                      key={tid}
                      className="rounded-lg border border-border-default bg-bg-secondary/40 overflow-hidden"
                    >
                      {/* Team card header */}
                      <div className="px-5 py-3 border-b border-border-default flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => toggleCollapse(tid)}
                          className="flex items-center gap-2 min-w-0 text-left hover:text-button-primary"
                        >
                          {isCollapsed
                            ? <ChevronRight className="w-4 h-4 shrink-0" />
                            : <ChevronDown className="w-4 h-4 shrink-0" />}
                          <span className="text-base font-semibold truncate">
                            {t?.name || "Team"}
                          </span>
                          {t?.short_name && (
                            <span className="text-xs text-text-secondary font-mono">
                              {t.short_name}
                            </span>
                          )}
                          {t?.nationality_name && (
                            <span className="text-xs text-text-secondary">
                              · {t.nationality_name}
                            </span>
                          )}
                        </button>
                        <div className="flex items-center gap-2 shrink-0 text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-primary border border-border-default">
                            <Users className="w-3 h-3" /> {roster.length} player{roster.length === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            onClick={() => resetToMasterSquad(tid)}
                            title="Reset roster to the team's master squad"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-secondary"
                          >
                            <RotateCcw className="w-3 h-3" /> Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTeam(tid)}
                            title="Remove this team from the pack"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>

                      {/* Player picker inside the team card */}
                      {!isCollapsed && (
                        <div className="px-5 py-4">
                          <CatalogPicker
                            fetchFn={(params) => listCatalogPlayers(params)}
                            selectedIds={roster}
                            onChange={(ids) => setPlayersForTeam(tid, ids)}
                            placeholder={`Add / remove players in ${t?.short_name || "this team"}…`}
                            emptyHint="No players match. Try a different search."
                            renderRow={(p) => (
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="min-w-0 flex items-center gap-2">
                                  <span className="font-medium">{playerDisplay(p)}</span>
                                  {p.role && (
                                    <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${ROLE_TONE[p.role] || ""}`}>
                                      {roleBadge(p.role)}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-text-secondary shrink-0">
                                  {[p.gender, p.nationality_name].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                            )}
                            renderChip={(p) => <span>{playerDisplay(p)}</span>}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
      </>
    </WizardShell>
  );
}
